import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { AppShell } from '@/components/layout/AppShell';
import { LearnsHero } from '@/components/learn/LearnsHero';
import { LearnsFeaturedCard } from '@/components/learn/LearnsFeaturedCard';
import { LearnsTopicChips } from '@/components/learn/LearnsTopicChips';
import { LearnsArticleGrid } from '@/components/learn/LearnsArticleGrid';
import { articlesService } from '@/services/articles.service';
import { ApiError } from '@/services/http';
import type { LearnsTopicId } from '@/constants/learns-data';
import type { ArticleView } from '@/types/api';

/**
 * LearnsPage — `/learns`. The Ollie Learns topic browser and discovery
 * hub. Data flow:
 *
 *   1. On mount, fire two fetches in parallel:
 *        - `/articles/featured` for the hero card
 *        - `/articles?limit=100` for the grid (the full set of live
 *          articles; we filter client-side because the topic chip row
 *          expects instantaneous switches)
 *   2. The grid excludes the featured article by id so it never appears
 *      twice.
 *   3. Topic filter is local; "all" shows every non-featured live article.
 *
 * Errors surface as toasts — the page still renders with whatever data
 * did arrive, so a featured-fetch failure doesn't take the grid down.
 */
export function LearnsPage() {
  const [activeTopic, setActiveTopic] = useState<'all' | LearnsTopicId>('all');
  const [featured, setFeatured] = useState<ArticleView | null>(null);
  const [articles, setArticles] = useState<ArticleView[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const [featuredRes, listRes] = await Promise.allSettled([
          articlesService.featured(),
          articlesService.list({ limit: 100 }),
        ]);

        if (cancelled) return;

        if (featuredRes.status === 'fulfilled') {
          setFeatured(featuredRes.value);
        } else {
          setFeatured(null);
          // Not fatal — the empty state renders when featured is null.
          console.warn('Featured fetch failed:', featuredRes.reason);
        }

        if (listRes.status === 'fulfilled') {
          setArticles(listRes.value.articles);
        } else {
          const reason = listRes.reason;
          toast.error(reason instanceof ApiError ? reason.message : 'Could not load articles.');
          setArticles([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const gridArticles = useMemo(() => {
    const featuredId = featured?.id;
    const withoutFeatured = featuredId ? articles.filter((a) => a.id !== featuredId) : articles;
    if (activeTopic === 'all') return withoutFeatured;
    return withoutFeatured.filter((a) => a.topic === activeTopic);
  }, [featured, articles, activeTopic]);

  return (
    <AppShell mode="kid" className="mx-auto w-full max-w-[1040px] px-8 pb-20 pt-7">
      {/* 1. Hero */}
      <LearnsHero />

      {/* 2. Featured article card */}
      <LearnsFeaturedCard article={featured} />

      {/* 3. Topic browser */}
      <LearnsTopicChips
        activeTopic={activeTopic}
        onChange={setActiveTopic}
        articleCount={gridArticles.length}
      />

      {/* 4. Article grid */}
      <div style={{ marginTop: '4px' }}>
        {loading && articles.length === 0 ? (
          <div
            className="text-center"
            style={{
              padding: '48px 0',
              fontFamily: 'Inter, sans-serif',
              color: '#8A7F72',
            }}
          >
            Loading articles…
          </div>
        ) : (
          <LearnsArticleGrid articles={gridArticles} />
        )}
      </div>
    </AppShell>
  );
}
